/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useDateFormatter, useTranslations } from "@i18n/react";
import { useMixpanel } from "@packages/ab";
import type { AnalyticsContext } from "@packages/analytics";
import { useAnalytics } from "@packages/analytics";
import type {
  BaseSectionItem,
  ReferenceType,
  Section as SectionSchemaType,
  SectionItem,
} from "@packages/sdk";
import { splitArray } from "@packages/sdk";
import type { format } from "date-fns/format";
import type { JSX } from "react";
import { forwardRef } from "react";

import { spacing } from "../../../../../../global/emotion";
import type {
  CarouselApi,
  UseCarouselProps,
} from "../../../../../hooks/useCarousel";
import { useIsDesktopViewport } from "../../../../../hooks/useIsDesktopViewport";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import { Container } from "../../../../blocks/layout/Container/Container.emotion";
import type { GridProps } from "../../../../blocks/layout/Grid/Grid.emotion";
import { Grid } from "../../../../blocks/layout/Grid/Grid.emotion";
import { GridItem } from "../../../../blocks/layout/GridItem/GridItem.emotion";
import { Stack } from "../../../../blocks/layout/Stack/Stack.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { getSectionItems } from "./getSectionItems";
import { SectionCarousel } from "./SectionCarousel/SectionCarousel.emotion";

export type BaseGetSectionItemProps<
  ItemType extends BaseSectionItem = SectionItem,
> = Pick<
  SectionProps,
  "itemTypeLinkSearchParams" | "link" | "mutate" | "page"
> & {
  analytics?: AnalyticsContext;
  item: ItemType;
  section: Partial<SectionSchemaType>;
  t: ReturnType<typeof useTranslations>;
  screenName?: string;
  format?: typeof format;
};

const hallowElement: HallowElement = "div";

const paddingBottomStyle: Interpolation = {
  paddingBottom: spacing.m,
};
const paddingTopStyle: Interpolation = {
  paddingTop: spacing.m,
};
const rightActionsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};
const stackContentSmallStyle: Interpolation = {
  gap: spacing.m,
};
const topContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  justifyContent: "space-between",
};

/**
 * TODO: Integrate MediaPlayer
 * TODO: ShareModal(s)
 */
export type SectionProps<ItemType extends BaseSectionItem = BaseSectionItem> =
  WithAsChild<HallowElementProps<typeof hallowElement>> & {
    itemTypeLinkSearchParams?: Partial<
      Record<ReferenceType, Record<string, string>>
    >;
    link?: boolean;
    // mediaPlayerContent?: any
    mutate?: boolean;
    overrideSize?: CarouselApi["size"] | GridProps["size"];
    padding?: boolean;
    section: Partial<SectionSchemaType>;
    page?: {
      id?: number;
      title?: string;
      slug?: string;
    };
    // share?: boolean;
    screenName?: string;
    showUnsupportedSections?: boolean;
    getSectionItemsFn?: typeof getSectionItems;
    carouselProps?: Partial<UseCarouselProps>;
  };

export const Section = forwardRef<any, SectionProps>(
  (
    {
      itemTypeLinkSearchParams = {},
      link = true,
      mutate = true,
      overrideSize,
      padding = true,
      section,
      page,
      screenName,
      showUnsupportedSections = false,
      getSectionItemsFn = getSectionItems,
      carouselProps,
      ...props
    },
    ref,
  ) => {
    const analytics = useAnalytics();
    const t = useTranslations();
    const isDesktop = useIsDesktopViewport();
    const formatDate = useDateFormatter();
    const { flags } = useMixpanel();

    const { items, rightActions, size, TitleLink } = getSectionItemsFn({
      analytics,
      itemTypeLinkSearchParams,
      link,
      mutate,
      section,
      page,
      screenName,
      t,
      isDesktop,
      format: formatDate,
      showUnsupportedSections,
      flags,
    });
    let Layout: JSX.Element | null;

    if (items?.length)
      switch (section.type) {
        case "carousel":
        case "stacked_carousel":
          Layout = (
            <SectionCarousel
              size={overrideSize ?? size}
              startIndex={section.start_index ?? 0}
              apiProps={carouselProps}
            >
              {section.type === "stacked_carousel"
                ? splitArray({ array: items, size: 3 }).map(
                    (itemsSplit, index) => (
                      <Stack
                        key={`${section.id}_${itemsSplit.map((i) => i.key).join("-")}`}
                        css={stackContentSmallStyle}
                      >
                        {itemsSplit}
                      </Stack>
                    ),
                  )
                : items}
            </SectionCarousel>
          );
          break;
        case "grid":
          Layout = (
            <Container paddingX={padding}>
              <Grid
                size={overrideSize ?? size}
                css={[paddingBottomStyle, paddingTopStyle]}
              >
                {items.map((item, index) => (
                  <GridItem key={`${section.id}${index}`}>{item}</GridItem>
                ))}
              </Grid>
            </Container>
          );
          break;
        default:
          if (showUnsupportedSections)
            return (
              <Container
                ref={ref}
                paddingX={padding}
                css={[paddingTopStyle, paddingBottomStyle]}
                {...props}
              >
                <Text as="h2" overflow="ellipsis" size="m" type="headline">
                  {section.title}
                </Text>
                <Text>
                  ID {section.id}. Unsupported section type {section.type}.
                </Text>
              </Container>
            );
          return null;
      }
    else if (showUnsupportedSections)
      return (
        <Container
          ref={ref}
          paddingX={padding}
          css={[paddingTopStyle, paddingBottomStyle]}
          {...props}
        >
          <Text as="h2" overflow="ellipsis" size="m" type="headline">
            {section.title}
          </Text>
          <Text>ID {section.id}. No items.</Text>
        </Container>
      );
    else return null;

    if (
      (section.item_type === "streak" ||
        section.item_type === "text_summary" ||
        section.title_link?.toLowerCase().includes("download")) &&
      !showUnsupportedSections
    ) {
      return null;
    }

    return (
      <Stack ref={ref} {...props}>
        {section.title && (
          <Container paddingX={padding} css={paddingTopStyle}>
            <div css={topContainerStyle}>
              <TitleLink>
                <Text as="h2" overflow="ellipsis" size="m" type="headline">
                  {section.title}
                </Text>
              </TitleLink>
              {rightActions.length > 0 && (
                <div css={rightActionsContainerStyle}>{rightActions}</div>
              )}
            </div>
          </Container>
        )}
        {Layout}
      </Stack>
    );
  },
);

Section.displayName = "Section";
